import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { IExamQuestionAdminMetaVm } from '../../../shared/models/exam-question-admin-meta-vm';
import { IDetailsControl } from 'fas-admin/lib/shared/models/details-control';
import { GetQuestionCategoriesService } from '../../../shared/api/get-question-categories.service';
import { PutQuestionBaseService } from '../../../shared/api/put-question-base.service';
import { ExplainQuestionService } from '../../../shared/api/explain-question.service';
import { FormatDateService } from '../../../shared/services/format-date.service';
import { SetColorsService } from '../../../shared/services/set-colors.service';
import {
  examCategoriesOptions,
  knowledgeTypeOptions,
  pointsOptions,
} from '../../../shared/helpers/constant-options';
import { IQuestionCategories } from '../../../shared/models/question-categories';
import { IExplainQuestionCreateIm } from '../../../../api/explain-question-create-im.model';
import { IExplainQuestionUpdateIm } from '../../../../api/explain-question-update-im.model';
import { environment } from '../../../../environments/environment';
import { IExamQuestionAdminMetaUm } from '../../../shared/models/exam-question-admin-meta-up';
import { QuestionBaseService } from '../../../../api/api-services/question-base.service';
import { IGovAdminQuestionDm } from '../../../../api/gov-admin-question-dm.model';
import { QuestionReviewService } from '../../../../api/api-services/question-review.service';
import { QuestionDatabaseDataService } from '../question-database-data.service';
import { IExamViewModel } from '../../../../api/exam-view-model.model';
import { lacksUa as questionLacksUa, missingTranslations } from '../question-translations';

@Component({
  selector: 'app-question-details',
  templateUrl: './question-details.component.html',
  styleUrl: './question-details.component.scss',
})
export class QuestionDetailsComponent implements OnInit {
  @Input() questionList!: IExamQuestionAdminMetaVm[];
  @Input() question!: IExamQuestionAdminMetaVm;
  @Input() questionIndex!: number;
  @Input() detailsControl: IDetailsControl = {
    isEditing: false,
    isOpen: false,
  };

  @Output('inicializeQuestionList') inicializeQuestionList: EventEmitter<any> =
    new EventEmitter();
  @Output() detailsControlChange = new EventEmitter<any>();

  private _getQuestionCategories = inject(GetQuestionCategoriesService);
  private _putQuestionBase = inject(PutQuestionBaseService);
  private _explainQuestionService = inject(ExplainQuestionService);
  private _messageService = inject(MessageService);
  private _questionBaseService = inject(QuestionBaseService);
  private _questionReviewService = inject(QuestionReviewService);
  private _formatDate = inject(FormatDateService);
  private _setColors = inject(SetColorsService);
  private _fb = inject(FormBuilder);
  private _http = inject(HttpClient);
  private _questionDatabaseDataService = inject(QuestionDatabaseDataService);

  public examCategoriesOptions = examCategoriesOptions;
  public knowledgeTypeOptions = knowledgeTypeOptions;
  public questionCategories!: IQuestionCategories[];
  public availableExams: IExamViewModel[] = this._questionDatabaseDataService.availableExams();
  public questionUpdateForm!: FormGroup;
  public explainFormPL!: FormGroup;
  public explainFormENG!: FormGroup;
  public explainFormDE!: FormGroup;
  public explainFormUA!: FormGroup;
  public pointsOptions = pointsOptions;
  public uploadedFiles: any[] = [];

  public environment = environment;
  public answerOptions: any;

  public autoTranslation: boolean = true;
  public editingExplain: { [key: string]: boolean } = {
    pl: false,
    eng: false,
    de: false,
    ua: false,
  };
  public savingExplain: { [key: string]: boolean } = {
    pl: false,
    eng: false,
    de: false,
    ua: false,
  };

  public loadingSaveQuestion: boolean = false;
  public editingMedia: boolean = false;
  public savingMedia: boolean = false;
  public selectedMediaFile: File | null = null;
  public mediaLoading: boolean = false;

  ngOnInit(): void {
    this._getQuestionCategories.getQuestionsCategories$().subscribe({
      next: (response) => {
        if (Array.isArray(response)) {
          this.questionCategories = response;
        } else {
          this.questionCategories = [response];
        }
      },

      error: (error) => {
        console.error('Error occurred: ', error);
      },
    });

    this.availableExams = this._questionDatabaseDataService.availableExams();

    this.setFormStructure();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.question) {
      if (changes['question']) {
        this.availableExams = this._questionDatabaseDataService.availableExams();
        this.mediaLoading = !!(this.question?.videoLink || this.question?.imageLink);
        this.setFormStructure();
      }
    }
  }

  showSuccess() {
    this._messageService.add({
      severity: 'success',
      detail: 'Pytanie zaktualizowane poprawnie',
    });
  }

  setFormStructure(): void {
    const categoryArray = this.convertCategoryStringToArray(this.question?.examCategories ?? undefined);

    this.questionUpdateForm = this._fb.group({
      knowledgeTypeId: [
        this.question?.knowledgeTypeId ?? null,
        [Validators.required],
      ],
      questionCategoryGuid: [
        this.question?.questionCategoryGuid ?? null,
        [Validators.required],
      ],
      points: [this.question?.points ?? null, [Validators.required]],
      category: [categoryArray, [Validators.required]],

      question: [this.question?.question ?? '', [Validators.required]],
      questionENG: [this.question?.questionENG ?? ''],
      questionDE: [this.question?.questionDE ?? ''],
      questionUA: [this.question?.questionUA ?? ''],
      // Ticked for a question still without Ukrainian too: whatever the editor types there is not the ministry's.
      uaMachineTranslated: [!!this.question?.uaMachineTranslated || !this.question?.questionUA?.trim()],

      answerA: [this.question?.answerA ?? ''],
      answerENGA: [this.question?.answerENGA ?? ''],
      answerDEA: [this.question?.answerDEA ?? ''],
      answerUAA: [this.question?.answerUAA ?? ''],

      answerB: [this.question?.answerB ?? ''],
      answerENGB: [this.question?.answerENGB ?? ''],
      answerDEB: [this.question?.answerDEB ?? ''],
      answerUAB: [this.question?.answerUAB ?? ''],

      answerC: [this.question?.answerC ?? ''],
      answerENGC: [this.question?.answerENGC ?? ''],
      answerDEC: [this.question?.answerDEC ?? ''],
      answerUAC: [this.question?.answerUAC ?? ''],

      correctAnswer: [
        this.question?.correctAnswer ?? '',
        [Validators.required],
      ],
    });

    const initialKnowledgeType =
      this.questionUpdateForm.get('knowledgeTypeId')?.value;
    this.updateAnswerValidation(initialKnowledgeType);

    this.questionUpdateForm
      .get('knowledgeTypeId')
      ?.valueChanges.subscribe((value: number | string) => {
        const knowledgeTypeId = Number(value);
        this.updateAnswerValidation(knowledgeTypeId);
        this.questionUpdateForm.get('correctAnswer')?.setValue('');

        if (knowledgeTypeId === 1) {
          this.questionUpdateForm.get('answerA')?.setValue('');
          this.questionUpdateForm.get('answerB')?.setValue('');
          this.questionUpdateForm.get('answerC')?.setValue('');

          this.questionUpdateForm.get('answerENGA')?.setValue('');
          this.questionUpdateForm.get('answerENGB')?.setValue('');
          this.questionUpdateForm.get('answerENGC')?.setValue('');

          this.questionUpdateForm.get('answerDEA')?.setValue('');
          this.questionUpdateForm.get('answerDEB')?.setValue('');
          this.questionUpdateForm.get('answerDEC')?.setValue('');

          this.questionUpdateForm.get('answerUAA')?.setValue('');
          this.questionUpdateForm.get('answerUAB')?.setValue('');
          this.questionUpdateForm.get('answerUAC')?.setValue('');

          this.answerOptions = [
            { value: 'Tak', label: 'Tak' },
            { value: 'Nie', label: 'Nie' },
          ];
        } else if (knowledgeTypeId === 2) {
          const answerA =
            this.questionUpdateForm.get('answerA')?.value ||
            this.question?.answerA ||
            'A';
          const answerB =
            this.questionUpdateForm.get('answerB')?.value ||
            this.question?.answerB ||
            'B';
          const answerC =
            this.questionUpdateForm.get('answerC')?.value ||
            this.question?.answerC ||
            'C';

          this.answerOptions = [
            { value: 'A', label: answerA },
            { value: 'B', label: answerB },
            { value: 'C', label: answerC },
          ];
        }
      });

    this.explainFormPL = this._fb.group({
      explainContent: [this.question?.explainPL ?? ''],
      autoTranslate: [true],
    });

    this.explainFormENG = this._fb.group({
      explainContent: [this.question?.explainENG ?? ''],
    });

    this.explainFormDE = this._fb.group({
      explainContent: [this.question?.explainDE ?? ''],
    });

    this.explainFormUA = this._fb.group({
      explainContent: [this.question?.explainUA ?? ''],
    });

    if (this.question) {
      this.answerOptions = this.setAnswerOptions();
    }
  }

  private updateAnswerValidation(
    knowledgeType: number | null | undefined
  ): void {
    const answerA = this.questionUpdateForm.get('answerA');
    const answerB = this.questionUpdateForm.get('answerB');
    const answerC = this.questionUpdateForm.get('answerC');

    if (knowledgeType === 2) {
      answerA?.setValidators([Validators.required]);
      answerB?.setValidators([Validators.required]);
      answerC?.setValidators([Validators.required]);
    } else {
      answerA?.clearValidators();
      answerB?.clearValidators();
      answerC?.clearValidators();
    }

    answerA?.updateValueAndValidity();
    answerB?.updateValueAndValidity();
    answerC?.updateValueAndValidity();
  }

  saveQuestion() {
    this.loadingSaveQuestion = true;
    
    const selectedExams = Array.isArray(this.questionUpdateForm.value.category) 
    ? this.questionUpdateForm.value.category 
    : [];
    const categoryString = selectedExams.map((e: any) => e.examName).join(',');
    
    const dataToPut: IExamQuestionAdminMetaUm = {
      guid: this.question.guid,
      examPoints: this.questionUpdateForm.value.points,
      questionCategoryGuid: this.questionUpdateForm.value.questionCategoryGuid,
      knowledgeType: this.questionUpdateForm.value.knowledgeTypeId,
      category: categoryString,
      
      questionText: this.questionUpdateForm.value.question,
      questionDE: this.questionUpdateForm.value.questionDE,
      questionENG: this.questionUpdateForm.value.questionENG,
      questionUA: this.questionUpdateForm.value.questionUA,
      uaMachineTranslated: !!this.questionUpdateForm.value.uaMachineTranslated,
      
      answerA: this.questionUpdateForm.value.answerA,
      answerENGA: this.questionUpdateForm.value.answerENGA,
      answerDEA: this.questionUpdateForm.value.answerDEA,
      answerUAA: this.questionUpdateForm.value.answerUAA,
      
      answerB: this.questionUpdateForm.value.answerB,
      answerENGB: this.questionUpdateForm.value.answerENGB,
      answerDEB: this.questionUpdateForm.value.answerDEB,
      answerUAB: this.questionUpdateForm.value.answerUAB,
      
      answerC: this.questionUpdateForm.value.answerC,
      answerENGC: this.questionUpdateForm.value.answerENGC,
      answerDEC: this.questionUpdateForm.value.answerDEC,
      answerUAC: this.questionUpdateForm.value.answerUAC,
      
      correctAnswer: this.questionUpdateForm.value.correctAnswer,

      media: this.question.imageLink || this.question.videoLink || '',
    };
    
    this._putQuestionBase.putQuestionsBase$(dataToPut).subscribe({
      next: (response) => {
        this.question.knowledgeTypeId = dataToPut.knowledgeType;
        this.question.examCategories = dataToPut.category;
        this.question.points = dataToPut.examPoints;
        this.question.questionCategoryGuid = dataToPut.questionCategoryGuid;
        this.question.question = dataToPut.questionText;
        this.question.questionENG = dataToPut.questionENG;
        this.question.questionDE = dataToPut.questionDE;
        this.question.questionUA = dataToPut.questionUA;
        // As the API saves it: the flag needs a Ukrainian text, and keeps the time of the first translation.
        this.question.uaMachineTranslated = !!dataToPut.uaMachineTranslated && !!dataToPut.questionUA?.trim();
        this.question.uaMachineTranslatedAt = this.question.uaMachineTranslated
          ? this.question.uaMachineTranslatedAt ?? new Date().toISOString()
          : null;
        this.question.answerA = dataToPut.answerA || '';
        this.question.answerB = dataToPut.answerB || '';
        this.question.answerC = dataToPut.answerC || '';
        this.question.answerENGA = dataToPut.answerENGA || '';
        this.question.answerENGB = dataToPut.answerENGB || '';
        this.question.answerENGC = dataToPut.answerENGC || '';
        this.question.answerDEA = dataToPut.answerDEA || '';
        this.question.answerDEB = dataToPut.answerDEB || '';
        this.question.answerDEC = dataToPut.answerDEC || '';
        this.question.answerUAA = dataToPut.answerUAA || '';
        this.question.answerUAB = dataToPut.answerUAB || '';
        this.question.answerUAC = dataToPut.answerUAC || '';
        this.question.correctAnswer = dataToPut.correctAnswer;
        this.question.missingTranslations = missingTranslations(this.question);
        
        this.answerOptions = this.setAnswerOptions();
        this.updateListItem({
          knowledgeTypeId: dataToPut.knowledgeType,
          examCategories: dataToPut.category,
          question: dataToPut.questionText,
          questionENG: dataToPut.questionENG,
          questionDE: dataToPut.questionDE,
          questionUA: dataToPut.questionUA,
          points: dataToPut.examPoints,
          uaMachineTranslated: this.question.uaMachineTranslated,
          uaMachineTranslatedAt: this.question.uaMachineTranslatedAt,
          missingTranslations: this.question.missingTranslations,
          answerA: dataToPut.answerA || '',
          answerB: dataToPut.answerB || '',
          answerC: dataToPut.answerC || '',
          answerENGA: dataToPut.answerENGA || '',
          answerENGB: dataToPut.answerENGB || '',
          answerENGC: dataToPut.answerENGC || '',
          answerDEA: dataToPut.answerDEA || '',
          answerDEB: dataToPut.answerDEB || '',
          answerDEC: dataToPut.answerDEC || '',
          answerUAA: dataToPut.answerUAA || '',
          answerUAB: dataToPut.answerUAB || '',
          answerUAC: dataToPut.answerUAC || '',
          correctAnswer: dataToPut.correctAnswer,
        });
        this.detailsControl.isEditing = false;
        this.loadingSaveQuestion = false;
        this.showSuccess();
      },
      error: (error) => {
        console.error(error);
        this.detailsControl.isEditing = false;
        this.loadingSaveQuestion = false;
      },
    });
  }

  onUpload(event: any) {
    for (let file of event.files) {
      this.uploadedFiles.push(file);
    }
    this._messageService.add({
      severity: 'info',
      summary: 'Plik załadowany',
      detail: '',
    });
  }

  /** The ministry ships no (complete) Ukrainian text and nobody translated it yet. */
  lacksUa(question: IExamQuestionAdminMetaVm): boolean {
    return questionLacksUa(question);
  }

  formatDate(input: string) {
    return this._formatDate.formatDate(input);
  }

  getKnowledgeColor(knowledgeType: string): string {
    return this._setColors.getKnowledgeColor(knowledgeType);
  }

  hideDialog() {
    if (this.isSaving) return;
    this.detailsControl.isOpen = false;
    this.detailsControl.isEditing = false;
    this.detailsControlChange.emit(this.detailsControl);
  }

  editToggle() {
    if (this.isSaving) return;
    if (!this.detailsControl.isEditing && (this.editingMedia || this.isAnyExplainBeingEdited())) return;
    this.detailsControl.isEditing = !this.detailsControl.isEditing;
    this.detailsControlChange.emit(this.detailsControl);

    this.resetEditingStates();
    this.setFormStructure();
  }
  editOn() {
    if (this.isSaving || this.editingMedia || this.isAnyExplainBeingEdited()) return;
    this.detailsControl.isEditing = true;
    this.detailsControlChange.emit(this.detailsControl);
  }

  onSelectFile(event: any) {}

  moveUp() {
    if (this.isSaving || this.isAnyEditing) return;
    this.questionIndex = (this.questionIndex + 1) % this.questionList.length;
    this.question = this.questionList[this.questionIndex];
    this.mediaLoading = !!(this.question?.videoLink || this.question?.imageLink);

    this.setFormStructure();
    this.resetEditingStates();
    this.detailsControl.isEditing = false;
    this.detailsControlChange.emit(this.detailsControl);
  }

  moveDown() {
    if (this.isSaving || this.isAnyEditing) return;
    this.questionIndex =
      (this.questionIndex - 1 + this.questionList.length) %
      this.questionList.length;
    this.question = this.questionList[this.questionIndex];
    this.mediaLoading = !!(this.question?.videoLink || this.question?.imageLink);

    this.setFormStructure();
    this.resetEditingStates();
    this.detailsControl.isEditing = false;
    this.detailsControlChange.emit(this.detailsControl);
  }

  public setAnswerOptions() {
    if (this.question.knowledgeTypeId === 2) {
      const { answerA, answerB, answerC } = this.question;
      return [
        { value: 'A', label: answerA || 'A' },
        { value: 'B', label: answerB || 'B' },
        { value: 'C', label: answerC || 'C' },
      ];
    }

    return [
      { value: 'Tak', label: 'Tak' },
      { value: 'Nie', label: 'Nie' },
    ];
  }

  getCorrectAnswerOptions(): { label: string; value: string }[] {
    const knowledgeType = Number(
      this.questionUpdateForm.get('knowledgeTypeId')?.value
    );
    if (knowledgeType === 1) {
      return [
        { label: 'Tak', value: 'Tak' },
        { label: 'Nie', value: 'Nie' },
      ];
    }
    return [
      { label: 'A', value: 'A' },
      { label: 'B', value: 'B' },
      { label: 'C', value: 'C' },
    ];
  }

  onKnowledgeTypeChange(event: any): void {
  }

  get isSpecialistQuestion(): boolean {
    return Number(this.questionUpdateForm.get('knowledgeTypeId')?.value) === 2;
  }

  get isBasicQuestion(): boolean {
    return Number(this.questionUpdateForm.get('knowledgeTypeId')?.value) === 1;
  }

  get isSaving(): boolean {
    return this.loadingSaveQuestion || this.savingMedia || Object.values(this.savingExplain).some(v => v);
  }

  get isAnyEditing(): boolean {
    return this.detailsControl.isEditing || this.editingMedia || this.isAnyExplainBeingEdited();
  }

  // Explanation management methods
  toggleExplainEdit(language: string): void {
    this.editingExplain[language] = !this.editingExplain[language];
  }

  startExplainEdit(language: string): void {
    if (this.isSaving || this.detailsControl.isEditing || this.editingMedia || this.isAnyExplainBeingEdited()) return;
    this.editingExplain[language] = true;
  }

  cancelExplainEdit(language: string): void {
    if (this.savingExplain[language]) return;
    this.editingExplain[language] = false;
    // Reset form values to original
    switch (language) {
      case 'pl':
        this.explainFormPL.patchValue({
          explainContent: this.question?.explainPL ?? '',
        });
        break;
      case 'eng':
        this.explainFormENG.patchValue({
          explainContent: this.question?.explainENG ?? '',
        });
        break;
      case 'de':
        this.explainFormDE.patchValue({
          explainContent: this.question?.explainDE ?? '',
        });
        break;
      case 'ua':
        this.explainFormUA.patchValue({
          explainContent: this.question?.explainUA ?? '',
        });
        break;
    }
  }

  saveExplain(language: string): void {
    let form: FormGroup;
    let content: string;
    let languageCode: string;

    switch (language) {
      case 'pl':
        form = this.explainFormPL;
        languageCode = 'pl';
        break;
      case 'eng':
        form = this.explainFormENG;
        languageCode = 'en';
        break;
      case 'de':
        form = this.explainFormDE;
        languageCode = 'de';
        break;
      case 'ua':
        form = this.explainFormUA;
        languageCode = 'ua';
        break;
      default:
        return;
    }

    content = form.get('explainContent')?.value || '';

    if (!content.trim()) {
      this._messageService.add({
        severity: 'warn',
        detail: 'Wyjaśnienie nie może być puste',
      });
      return;
    }

    this.savingExplain[language] = true;

    const isNew = this.isNewExplanation(language);

    if (isNew) {
      this.createExplain(
        languageCode,
        content,
        language === 'pl' ? form.get('autoTranslate')?.value : false
      );
    } else {
      const autoTranslate =
        language === 'pl' ? form.get('autoTranslate')?.value : false;
      this.updateExplain(languageCode, content, autoTranslate);
    }
  }

  private isNewExplanation(language: string): boolean {
    switch (language) {
      case 'pl':
        return !this.question?.explainPL;
      case 'eng':
        return !this.question?.explainENG;
      case 'de':
        return !this.question?.explainDE;
      case 'ua':
        return !this.question?.explainUA;
      default:
        return true;
    }
  }

  private createExplain(
    languageCode: string,
    content: string,
    autoTranslate: boolean = false
  ): void {
    const data: IExplainQuestionCreateIm = {
      questionGuid: this.question.guid,
      explainContent: content,
      languageCode: languageCode,
      autoTranslate: autoTranslate,
    };

    this._explainQuestionService.updateExplain$(data).subscribe({
      next: (response) => {
        this.updateQuestionExplanations(response);
        this.resetEditingStates();
        this.resetSavingStates();
        this._messageService.add({
          severity: 'success',
          detail: 'Wyjaśnienie zostało dodane',
        });
      },
      error: (error) => {
        this.resetSavingStates();
        console.error('Error creating explanation:', error);
        this._messageService.add({
          severity: 'error',
          detail: 'Błąd podczas dodawania wyjaśnienia',
        });
      },
    });
  }

  private updateExplain(
    languageCode: string,
    content: string,
    autoTranslate: boolean = false
  ): void {
    const data: IExplainQuestionUpdateIm = {
      questionGuid: this.question.guid,
      explainContent: content,
      languageCode: languageCode,
      autoTranslate: autoTranslate,
    };

    this._explainQuestionService.updateExplain$(data).subscribe({
      next: (response) => {
        this.updateQuestionExplanations(response);
        this.resetEditingStates();
        this.resetSavingStates();
        this._messageService.add({
          severity: 'success',
          detail: 'Wyjaśnienie zostało zaktualizowane',
        });
      },
      error: (error) => {
        this.resetSavingStates();
        console.error('Error updating explanation:', error);
        this._messageService.add({
          severity: 'error',
          detail: 'Błąd podczas aktualizacji wyjaśnienia',
        });
      },
    });
  }

  private updateQuestionExplanations(response: any): void {
    this.question.explainPL = response.explainPL;
    this.question.explainENG = response.explainENG;
    this.question.explainDE = response.explainDE;
    this.question.explainUA = response.explainUA;

    // Update forms with new values
    this.explainFormPL.patchValue({ explainContent: response.explainPL });
    this.explainFormENG.patchValue({ explainContent: response.explainENG });
    this.explainFormDE.patchValue({ explainContent: response.explainDE });
    this.explainFormUA.patchValue({ explainContent: response.explainUA });

    this.updateListItem({
      explainPL: response.explainPL,
      explainENG: response.explainENG,
      explainDE: response.explainDE,
      explainUA: response.explainUA,
    });
  }

  private resetEditingStates(): void {
    Object.keys(this.editingExplain).forEach((key) => {
      this.editingExplain[key] = false;
    });
  }

  private resetSavingStates(): void {
    Object.keys(this.savingExplain).forEach((key) => {
      this.savingExplain[key] = false;
    });
  }

  hasAnyExplanation(): boolean {
    return !!(
      this.question?.explainPL ||
      this.question?.explainENG ||
      this.question?.explainDE ||
      this.question?.explainUA
    );
  }

  canAddOtherLanguages(): boolean {
    return !!this.question?.explainPL;
  }

  /** A request to flag or clear is in flight; the buttons wait for it. */
  public reviewSaving = false;

  /** Flags the question for "Pytania do sprawdzenia" or ticks it off; the reason is given from the list. */
  toggleReview() {
    if (this.reviewSaving) {
      return;
    }
    const needsReview = !this.question.needsReview;
    this.reviewSaving = true;
    this._questionReviewService
      .put({ questionGuid: this.question.guid, needsReview, note: null })
      .subscribe({
        next: (vm) => {
          this.reviewSaving = false;
          const patch: Partial<IExamQuestionAdminMetaVm> = {
            needsReview: vm.needsReview,
            reviewNote: vm.reviewNote,
            reviewRequestedAt: vm.reviewRequestedAt,
            reviewedAt: vm.reviewedAt,
            reviewedByEmail: vm.reviewedByEmail,
          };
          Object.assign(this.question, patch);
          this.updateListItem(patch);
          this._messageService.add({
            severity: 'success',
            detail: needsReview
              ? 'Pytanie oznaczone do sprawdzenia'
              : 'Pytanie oznaczone jako sprawdzone',
          });
        },
        error: (error) => {
          console.error('Error occurred:', error);
          this.reviewSaving = false;
          this._messageService.add({
            severity: 'error',
            detail: 'Nie udało się zapisać oznaczenia pytania',
          });
        },
      });
  }

  deleteQuestion() {
    const dataToDelete: IGovAdminQuestionDm = {
      questionGuid: this.question.guid,
      isDeleted: !this.question.isDeleted,
    };

    this._questionBaseService.delete(dataToDelete).subscribe({
      next: (res) => {
        this.question.isDeleted = !this.question.isDeleted;
        this.updateListItem({ isDeleted: this.question.isDeleted });
        this._messageService.add({
          severity: 'success',
          detail: this.question.isDeleted
            ? 'Pytanie zostało usunięte'
            : 'Pytanie zostało przywrócone',
        });
      },
    });
  }


  public enableMediaEditing(): void {
    if (this.isSaving || this.detailsControl.isEditing || this.isAnyExplainBeingEdited()) {
      return;
    }
    this.editingMedia = true;
  }

  public cancelMediaEditing(): void {
    if (this.savingMedia) return;
    this.editingMedia = false;
    this.selectedMediaFile = null;
  }

  public onMediaFileSelected(files: File | File[]): void {
    if (files instanceof File) {
      this.selectedMediaFile = files;
    } else if (Array.isArray(files) && files.length > 0) {
      this.selectedMediaFile = files[0];
    }
  }

  public async saveMedia(): Promise<void> {
    if (!this.selectedMediaFile) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Uwaga',
        detail: 'Proszę wybrać plik',
      });
      return;
    }

    this.savingMedia = true;

    try {
      const uploadedUrl = await this.uploadFileToServer(this.selectedMediaFile);

      const dataToPut: IExamQuestionAdminMetaUm = {
        guid: this.question.guid,
        examPoints: this.question.points,
        questionCategoryGuid: this.question.questionCategoryGuid || '',
        knowledgeType: this.question.knowledgeTypeId || 1,
        category: this.question.examCategories || '',
        questionText: this.question.question,
        questionDE: this.question.questionDE,
        questionENG: this.question.questionENG,
        questionUA: this.question.questionUA,
        answerA: this.question.answerA,
        answerENGA: this.question.answerENGA,
        answerDEA: this.question.answerDEA,
        answerUAA: this.question.answerUAA,
        answerB: this.question.answerB,
        answerENGB: this.question.answerENGB,
        answerDEB: this.question.answerDEB,
        answerUAB: this.question.answerUAB,
        answerC: this.question.answerC,
        answerENGC: this.question.answerENGC,
        answerDEC: this.question.answerDEC,
        answerUAC: this.question.answerUAC,
        correctAnswer: this.question.correctAnswer,
        media: uploadedUrl,
      };

      this._putQuestionBase.putQuestionsBase$(dataToPut).subscribe({
        next: (response) => {
          const fileExtension = this.selectedMediaFile?.name.split('.').pop()?.toLowerCase();
          if (fileExtension === 'mp4' || fileExtension === 'avi' || fileExtension === 'mov' || fileExtension === 'webm') {
            this.question.videoLink = uploadedUrl;
            this.question.imageLink = '';
          } else {
            this.question.imageLink = uploadedUrl;
            this.question.videoLink = '';
          }

          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: 'Media zostały zaktualizowane',
          });

          this.updateListItem({
            imageLink: this.question.imageLink,
            videoLink: this.question.videoLink,
            hasImage: !!this.question.imageLink,
            hasVideo: !!this.question.videoLink,
          });
          this.editingMedia = false;
          this.selectedMediaFile = null;
          this.savingMedia = false;
        },
        error: (error) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się zaktualizować mediów',
          });
          this.savingMedia = false;
        },
      });
    } catch (error) {
      this.savingMedia = false;
    }
  }

  public removeMedia(): void {
    this.savingMedia = true;

    const dataToPut: IExamQuestionAdminMetaUm = {
      guid: this.question.guid,
      examPoints: this.question.points,
      questionCategoryGuid: this.question.questionCategoryGuid || '',
      knowledgeType: this.question.knowledgeTypeId || 1,
      category: this.question.examCategories || '',
      questionText: this.question.question,
      questionDE: this.question.questionDE,
      questionENG: this.question.questionENG,
      questionUA: this.question.questionUA,
      answerA: this.question.answerA,
      answerENGA: this.question.answerENGA,
      answerDEA: this.question.answerDEA,
      answerUAA: this.question.answerUAA,
      answerB: this.question.answerB,
      answerENGB: this.question.answerENGB,
      answerDEB: this.question.answerDEB,
      answerUAB: this.question.answerUAB,
      answerC: this.question.answerC,
      answerENGC: this.question.answerENGC,
      answerDEC: this.question.answerDEC,
      answerUAC: this.question.answerUAC,
      correctAnswer: this.question.correctAnswer,
      media: '',
    };

    this._putQuestionBase.putQuestionsBase$(dataToPut).subscribe({
      next: (response) => {
        this.question.videoLink = '';
        this.question.imageLink = '';

        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Media zostały usunięte',
        });

        this.updateListItem({ imageLink: '', videoLink: '', hasImage: false, hasVideo: false });
        this.savingMedia = false;
      },
      error: (error) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się usunąć mediów',
        });
        this.savingMedia = false;
      },
    });
  }

  private convertCategoryStringToArray(categoryString: string | undefined): IExamViewModel[] {
    if (!categoryString) return [];
    
    const categoryNames = categoryString.split(',').map(name => name.trim());
    return this.availableExams.filter(exam => 
      categoryNames.includes(exam.examName)
    );
  }

  private async uploadFileToServer(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('publicVisible', 'true');

      const response = await firstValueFrom(
        this._http.post<any>(
          `${environment.webApiUrl}/UploadMedia`,
          formData
        )
      );

      if (response.success) {
        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Plik został przesłany',
        });
        const urlParams = new URLSearchParams(response.fileUrl.split('?')[1]);
        const fileName = urlParams.get('imageName') || response.fileUrl.split('/').pop() || response.fileUrl;
        return fileName;
      } else {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się przesłać pliku',
        });
        throw new Error('Upload failed');
      }
    } catch (error) {
      this._messageService.add({
        severity: 'error',
        summary: 'Błąd',
        detail: 'Wystąpił błąd podczas przesyłania pliku',
      });
      throw error;
    }
  }

  private isAnyExplainBeingEdited(): boolean {
    return Object.values(this.editingExplain).some((editing) => editing);
  }

  private updateListItem(updatedFields: Partial<IExamQuestionAdminMetaVm>): void {
    this._questionDatabaseDataService.questionsList.update(list =>
      list.map(q => q.guid === this.question.guid ? { ...q, ...updatedFields } : q)
    );
  }
}
